import { createSlice } from '@reduxjs/toolkit'

import type { RootState } from '..'
import { fetchTrainDataAction } from '../actions/wagons'

// These types have been copied to backend to src/punctuality/wagons.ts
export interface TrainInstanceRef {
  trainNumber: string
  businessDate: string
  isDifferentTrain: boolean
}

export interface Vehicle {
  vehicleNumber: string
  vehicleEVN: string | null
  type: string
}

export interface TrainPartVehicle {
  salesNumber: string | null
  allocatedVehicleType: string
  assignedVehicle: Vehicle | null
  previousTrainInstance: TrainInstanceRef | null
  nextTrainInstance: TrainInstanceRef | null
}

export interface InfraPoint {
  shortCode: string
}

export interface TrainPart {
  departureInfraPoint: InfraPoint
  arrivalInfraPoint: InfraPoint
  vehicles: TrainPartVehicle[]
}

export interface TrainInstance {
  trainNumber: string
  businessDate: string
  trainParts: TrainPart[] | null
}

export type TrainIdentifier = string

type FetchedTrains = Record<
  TrainIdentifier,
  {
    data?: TrainInstance
    state: 'loading' | 'loaded' | 'error'
    error?: string
  }
>

export type WagonSectionState = {
  fetchedTrains: FetchedTrains
}

const initialState: WagonSectionState = {
  fetchedTrains: {},
}

const emptyTrainParts: TrainPart[] = []

export const createTrainId = (trainNumber: string, trainDate: string) =>
  `${trainNumber}@${trainDate}`

const wagonSlice = createSlice({
  name: 'wagonSection',
  initialState,
  reducers: {
    resetWagonState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTrainDataAction.pending, (state, { meta }) => {
      const trainId = createTrainId(meta.arg.trainNumber, meta.arg.businessDate)
      state.fetchedTrains[trainId] = {
        data: undefined,
        state: 'loading',
      }
    })

    builder.addCase(fetchTrainDataAction.fulfilled, (state, { payload, meta }) => {
      const trainId = createTrainId(meta.arg.trainNumber, meta.arg.businessDate)
      state.fetchedTrains[trainId] = {
        data: payload,
        state: 'loaded',
      }
    })

    builder.addCase(fetchTrainDataAction.rejected, (state, { payload, meta }) => {
      const trainId = createTrainId(meta.arg.trainNumber, meta.arg.businessDate)
      if (state.fetchedTrains[trainId]) {
        state.fetchedTrains[trainId].state = 'error'
        state.fetchedTrains[trainId].error = payload?.message || 'Unknown error'
      }
    })
  },
})

export const { resetWagonState } = wagonSlice.actions

// Selectors
export const getCurrentTrain = (state: RootState) => {
  const trainNumber = state.punctuality.selectedTrainNumber
  const trainDate = state.punctuality.selectedTrainDate
  const currentTrainId = createTrainId(trainNumber, trainDate)
  const trainEntry = state.wagonSection.fetchedTrains[currentTrainId]

  if (!trainEntry) return null

  return trainEntry
}

export const getCurrentTrainParts = (state: RootState): TrainPart[] => {
  return getCurrentTrain(state)?.data?.trainParts || emptyTrainParts
}

export default wagonSlice.reducer
